import { useMemo, useState } from 'react';

import { addDays, isSameDay } from 'date-fns';
import { CalendarIcon, ClockIcon, GlobeIcon } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';

import { TruncatedText } from '@/components/atoms/TruncatedText/TruncatedText';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { PopoverCombobox } from '@/components/ui/custom/PopoverCombobox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { timezones } from '@/util/timezones';

import {
  MAX_SCHEDULE_DELAY_MS,
  type TimeOption,
  buildScheduledAt,
  formatDateLabel,
  formatScheduleDelay,
  getAvailableTimeOptions,
  getTimeOptions,
  getTimezoneLabel,
  getZonedToday,
  isValidScheduleDelay,
} from './utils';

interface Props {
  date: Date;
  /** 24-hour "HH:mm". */
  time: string;
  /** IANA timezone name. */
  timezone: string;
  onDateChange: (date: Date) => void;
  onTimeChange: (time: string) => void;
  onTimezoneChange: (timezone: string) => void;
}

// ~440 `Intl` computations — build lazily on first mount (not at module import
// time) and reuse across mounts; the labels are DST-stable per session.
let cachedTimezoneOptions: { value: string; label: string }[] | undefined;
const getTimezoneOptions = () =>
  (cachedTimezoneOptions ??= timezones.map((tz) => ({ value: tz, label: getTimezoneLabel(tz) })));

/**
 * Date + time + timezone row with the "from now" helper underneath. Fully
 * controlled: the owner holds the value and performs the POST. Times already
 * past for the selected date and timezone are not offered.
 */
const ScheduleTimeFields = ({
  date,
  time,
  timezone,
  onDateChange,
  onTimeChange,
  onTimezoneChange,
}: Props) => {
  const { t, i18n } = useTranslation();
  const [dateOpen, setDateOpen] = useState(false);
  const [selectOpenCount, setSelectOpenCount] = useState(0);

  const timezoneOptions = getTimezoneOptions();

  const allTimeOptions = useMemo(() => getTimeOptions(30, i18n.language), [i18n.language]);

  const availableTimeOptions = useMemo(
    () => getAvailableTimeOptions(date, timezone, new Date(), 30, i18n.language),
    // `selectOpenCount` re-filters against a fresh `now` on every dropdown open —
    // the modal can sit open long enough for the earliest slots to lapse.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [date, timezone, i18n.language, selectOpenCount]
  );

  /**
   * Moves the pick to the earliest remaining slot when it drops out of the list
   * for the new day or zone (e.g. 00:30 tomorrow → back to today). Snapping here
   * rather than in an effect keeps it in the same batch as the change itself.
   */
  const snapTimeInto = (options: TimeOption[]) => {
    const [firstAvailable] = options;
    if (firstAvailable && !options.some((option) => option.value === time)) {
      onTimeChange(firstAvailable.value);
    }
  };

  const handleDateChange = (selected: Date) => {
    onDateChange(selected);
    snapTimeInto(getAvailableTimeOptions(selected, timezone));
  };

  const handleTimezoneChange = (selected: string) => {
    onTimezoneChange(selected);
    const options = getAvailableTimeOptions(date, selected);
    if (options.length > 0) {
      snapTimeInto(options);
      return;
    }
    // The picked day can be entirely over in the new zone — 12:00 in Midway is
    // already tomorrow in Kiritimati. Move to the earliest day that still has a
    // slot, otherwise the picker strands itself with nothing selectable.
    const zonedToday = getZonedToday(selected);
    const target =
      getAvailableTimeOptions(zonedToday, selected).length > 0
        ? zonedToday
        : addDays(zonedToday, 1);
    onDateChange(target);
    snapTimeInto(getAvailableTimeOptions(target, selected));
  };

  // Radix takes the trigger's label from the matching item, so a value with no
  // item renders an empty trigger. Keep the current pick listed while it is out
  // of range — once today is exhausted there is nothing to snap to, and a slot
  // can lapse while the modal sits open; the helper text and the disabled
  // submit are what flag it.
  const timeOptions = useMemo(() => {
    if (availableTimeOptions.some((option) => option.value === time)) {
      return availableTimeOptions;
    }
    const selected = allTimeOptions.find((option) => option.value === time);
    if (!selected) {
      return availableTimeOptions;
    }
    // Zero-padded "HH:mm" orders lexicographically, so this keeps the lapsed pick
    // in chronological position rather than stranding it after the last slot.
    const insertAt = availableTimeOptions.findIndex((option) => option.value > selected.value);
    if (insertAt === -1) {
      return [...availableTimeOptions, selected];
    }
    return [
      ...availableTimeOptions.slice(0, insertAt),
      selected,
      ...availableTimeOptions.slice(insertAt),
    ];
  }, [allTimeOptions, availableTimeOptions, time]);

  const scheduledAt = buildScheduledAt(date, time, timezone);
  const isValid = isValidScheduleDelay(scheduledAt);
  // Not memoized so the "from now" text can't freeze a stale `now`.
  const delayText = formatScheduleDelay(scheduledAt, t, i18n.language);

  // "Today" and the calendar bounds follow the *selected* zone, matching how the
  // picked day is read back. The calendar only constrains the date; the 30s–14d
  // horizon is enforced on the combined value via the submit button.
  const zonedToday = getZonedToday(timezone);
  const maxDate = getZonedToday(timezone, new Date(Date.now() + MAX_SCHEDULE_DELAY_MS));

  const getDateLabel = () => {
    if (isSameDay(date, zonedToday)) {
      return t('scheduled_messages.today');
    }
    if (isSameDay(date, addDays(zonedToday, 1))) {
      return t('scheduled_messages.tomorrow');
    }
    return formatDateLabel(date, i18n.language);
  };

  return (
    <>
      <div className="flex items-center gap-2">
        <Popover open={dateOpen} onOpenChange={setDateOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline-neutral"
              className="min-w-0 flex-1 justify-start gap-2 font-normal"
              aria-label={t('scheduled_messages.date')}
            >
              <CalendarIcon size={16} className="text-muted-foreground" />
              <TruncatedText text={getDateLabel()} />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={date}
              onSelect={(selected) => {
                // A click on the selected day reads as a deselect — a schedule
                // always needs a date, so keep the current one and just close.
                if (selected) {
                  handleDateChange(selected);
                }
                setDateOpen(false);
              }}
              disabled={{ before: zonedToday, after: maxDate }}
            />
          </PopoverContent>
        </Popover>

        <Select
          value={time}
          onValueChange={onTimeChange}
          onOpenChange={(isOpen) => isOpen && setSelectOpenCount((count) => count + 1)}
        >
          <SelectTrigger className="w-32 gap-2" aria-label={t('scheduled_messages.time')}>
            <ClockIcon size={16} className="text-muted-foreground" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {timeOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <PopoverCombobox
          options={timezoneOptions}
          value={timezone}
          onChange={handleTimezoneChange}
          trigger={
            <Button
              variant="outline-neutral"
              className="min-w-0 flex-1 justify-start gap-2 font-normal"
              aria-label={t('scheduled_messages.timezone')}
            >
              <GlobeIcon size={16} className="text-muted-foreground" />
              <TruncatedText text={getTimezoneLabel(timezone, date)} />
            </Button>
          }
        />
      </div>

      <p className="text-sm text-muted-foreground">
        {isValid ? (
          <Trans
            i18nKey="scheduled_messages.send_time_helper"
            values={{ 0: delayText }}
            components={{ bold: <span className="font-semibold text-foreground" /> }}
          />
        ) : (
          t('scheduled_messages.delay_hint')
        )}
      </p>
    </>
  );
};

export default ScheduleTimeFields;
